import { Button, Input, Textarea, toast } from "@/components/kit";
import { Modal } from "@/components/modal";
import {
  FieldContent,
  FieldDescription,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import type { MarkReturnedDTO } from "@/modules/orders/domain/contracts";
import type { OrderDetailDTO } from "@/modules/orders/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { useEffect, useMemo, useState } from "react";
import { useMarkOrderReturned } from "../api/queries";

type OrderMarkReturnedDialogProps = {
  order: OrderDetailDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function parseCostInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

export function OrderMarkReturnedDialog({
  order,
  open,
  onOpenChange,
}: OrderMarkReturnedDialogProps) {
  const markReturned = useMarkOrderReturned();
  const [returnNote, setReturnNote] = useState("");
  const [returnShippingCost, setReturnShippingCost] = useState("");
  const [outboundCarrierCost, setOutboundCarrierCost] = useState("");

  useEffect(() => {
    if (!open) return;
    setReturnNote("");
    setReturnShippingCost("");
    setOutboundCarrierCost("");
  }, [open]);

  const totalLoss = useMemo(() => {
    const returnCost = parseCostInput(returnShippingCost) ?? 0;
    const outboundCost = parseCostInput(outboundCarrierCost) ?? 0;
    return returnCost + outboundCost;
  }, [returnShippingCost, outboundCarrierCost]);

  function handleSubmit() {
    const input: MarkReturnedDTO = {
      returnNote: returnNote.trim() || null,
      returnShippingCost: parseCostInput(returnShippingCost),
      outboundCarrierCost: parseCostInput(outboundCarrierCost),
    };

    markReturned.mutate(
      { id: order.id, input },
      {
        onSuccess: () => {
          toast.success(
            totalLoss > 0
              ? "ບັນທຶກຕີກັບແລ້ວ — ຄືນສະຕັອກ ແລະ ບັນທຶກຄ່າສົ່ງເສຍ"
              : "ບັນທຶກຕີກັບແລ້ວ — ຄືນສະຕັອກແລ້ວ",
          );
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error("ບັນທຶກບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ຂົນສົ່ງຕີກັບ"
      description="ລູກຄ້າບໍ່ໄປເອົາ — ສິນຄ້າກັບມາຮ້ານ ລະບົບຈະຄືນສະຕັອກ"
      size="md"
      footer={
        <>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={markReturned.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            variant="destructive"
            className="rounded-full"
            isLoading={markReturned.isPending}
            onClick={handleSubmit}
          >
            ຕີກັບ
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FieldLayout>
          <FieldLabel htmlFor="return-note">ໝາຍເຫດ (ຖ້າມີ)</FieldLabel>
          <FieldContent>
            <Textarea
              id="return-note"
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              placeholder="ລູກຄ້າບໍ່ໄປເອົາ..."
              rows={2}
            />
          </FieldContent>
        </FieldLayout>

        <div className="grid gap-4 sm:grid-cols-2">
          <FieldLayout>
            <FieldLabel htmlFor="return-shipping-cost">
              ຄ່າສົ່ງຕີກັບ (ຮ້ານຈ່າຍ)
            </FieldLabel>
            <FieldContent>
              <Input
                id="return-shipping-cost"
                type="number"
                min={0}
                step="1000"
                inputMode="decimal"
                value={returnShippingCost}
                onChange={(e) => setReturnShippingCost(e.target.value)}
                placeholder="0"
              />
            </FieldContent>
            <FieldDescription>ບໍ່ບັງຄັບ — ຖ້າມີຈະສ້າງລາຍຈ່າຍອັດຕະໂນມັດ</FieldDescription>
          </FieldLayout>

          <FieldLayout>
            <FieldLabel htmlFor="outbound-carrier-cost">
              ຄ່າສົ່ງໄປ (ຮ້ານຈ່າຍ)
            </FieldLabel>
            <FieldContent>
              <Input
                id="outbound-carrier-cost"
                type="number"
                min={0}
                step="1000"
                inputMode="decimal"
                value={outboundCarrierCost}
                onChange={(e) => setOutboundCarrierCost(e.target.value)}
                placeholder="0"
              />
            </FieldContent>
            <FieldDescription>ຄ່າຂົນສົ່ງຂາໄປທີ່ຮ້ານຈ່າຍແລ້ວ (ຖ້າຮູ້)</FieldDescription>
          </FieldLayout>
        </div>

        {totalLoss > 0 ? (
          <p className="rounded-lg bg-destructive/10 px-3 py-2 text-destructive text-sm">
            ຄ່າສົ່ງເສຍລວມ: {formatLak(totalLoss.toFixed(2))}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
